import { Component, Input, SimpleChanges } from '@angular/core';
import { ControlContainer, FormControl, FormGroupDirective } from '@angular/forms';
import { Observable, debounceTime } from 'rxjs';

export interface SearchOptions {
  key: string;
  primaryKey?: string;
  initialSearch?: boolean;
  format?: (value: string) => string;
  filter: (value: string) => Observable<{ label: string; value: string }[]>;
}

@Component({
  selector: 'app-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class SearchInputComponent {
  @Input()
  appearance?: 'fill' | 'outline' = 'outline';

  private primaryKeySyncInitialized = false;

  @Input()
  label: string = '';

  @Input()
  name: string = '';

  @Input()
  icon?: string = '';

  @Input()
  mask?: string = '';

  @Input()
  searchOptions?: SearchOptions;

  @Input()
  onChange?: (value: string) => void;

  public searchControl = new FormControl();
  public items: { value: string; label: string }[] = [];

  private selectedItemCache?: { value: string; label: string };

  constructor(
    private formGroupDirective: FormGroupDirective,
  ) { }

  ngOnInit() {
    if (this.searchOptions && this.searchOptions.initialSearch) {
      this.onSearch('');
    }

    this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
      this.onSearch(value ? value.trim() : '');
    });

    this.initPrimaryKeySync();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['searchOptions']) {
      this.initPrimaryKeySync();
    }
  }

  private initPrimaryKeySync() {
    const control = this.formGroupDirective.control.get(this.name);

    if (!control || !this.searchOptions?.primaryKey || this.primaryKeySyncInitialized) {
      return;
    }

    this.primaryKeySyncInitialized = true;

    const syncSelectedItem = (value: string) => {
      if (!value) {
        return;
      }

      const query = `${this.searchOptions!.primaryKey}=${value}`;

      this.searchOptions!.filter(query).subscribe((result) => {
        if (result) {
          this.items = result;
          this.selectedItemCache = result.find(item => item.value === value);
        }
      });
    };

    syncSelectedItem(control.value);
    control.valueChanges.subscribe((value) => syncSelectedItem(value));
  }

  private preserveSelectedItem() {
    const selectedValue = this.formGroupDirective.control.get(this.name)?.value;
    if (!selectedValue) return;

    const existsInItems = this.items.some(item => item.value === selectedValue);
    if (!existsInItems && this.selectedItemCache) {
      this.items = [this.selectedItemCache, ...this.items];
    }
  }

  onSearch(value?: string) {
    if (!this.searchOptions) {
      return;
    }
    
    if (value && this.searchOptions.format) {
      value = this.searchOptions.format(value);
    }
    
    const query = value ? `${this.searchOptions.key}=${value}` : '';
    
    this.searchOptions.filter(query).subscribe((result) => {
      if (result) {
        this.items = result;
        this.preserveSelectedItem();
      }
    });
  }

  change(value: string) {
    this.selectedItemCache = this.items.find(item => item.value === value);

    if (this.onChange) {
      this.onChange(value);
    }
  }

  handleFocus() {
    this.onSearch('');
  }
}
